import { findPileWithCard, type GameState } from '../../engine/game-state.js';
import type { SolitaireRules } from '../../engine/rules.js';
import { layoutFromMap, type PileSlot } from '../../render/three/layout.js';
import type { GameSpec, PileClickResult } from '../../render/three/game-app.js';
import {
  drawFromStock,
  hasAnyMove,
  isUncovered,
  PYRAMID_IDS,
  pyramidRules,
} from './rules.js';

const COL = 1.05;
const ROW = 0.42;            // ~30% of card visible per row; keeps 7-row pyramid compact
const Y_LIFT = 0.0015;       // sub-mm row stagger to break z-fighting
const SELECTED_LIFT = 0.06;  // subtle "this is selected" cue - most clicks
                             // auto-pair with waste, so the lift only shows
                             // when the user is mid-pair (no waste partner)

function pyramidSlots(): Record<string, PileSlot> {
  const out: Record<string, PileSlot> = {};
  for (let r = 1; r <= 7; r++) {
    for (let c = 1; c <= r; c++) {
      out[`r${r}c${c}`] = {
        x: (c - (r + 1) / 2) * COL,
        z: (r - 1) * ROW,
        y: (r - 1) * Y_LIFT,
        fanDown: false,
      };
    }
  }
  return out;
}

const ROW7_BOTTOM_Z = 6 * ROW + 0.7;
const STOCK_Z = ROW7_BOTTOM_Z + 0.18 + 0.7;

const slotMap: Record<string, PileSlot> = {
  ...pyramidSlots(),
  stock: { x: -0.6, z: STOCK_Z, y: 0, fanDown: false },
  waste: { x:  0.6, z: STOCK_Z, y: 0, fanDown: false },
  foundation: { x: 2.6, z: STOCK_Z, y: 0, fanDown: false },
};

const layout = layoutFromMap(slotMap);

// Selection state (UI-only; not part of GameState). Pyramid pairs require
// two card selections, but the engine's Move is one card - we track the
// active selection here and apply both halves of the pair manually.
let selectedId: string | null = null;

function discardCard(state: GameState, fromPileId: string): void {
  const fromPile = state.piles[fromPileId]!;
  const card = fromPile.cards.pop()!;
  state.piles.foundation!.cards.push(card);
}

function applyPair(state: GameState, pileAId: string, pileBId: string): boolean {
  const a = state.piles[pileAId]?.cards.at(-1);
  const b = state.piles[pileBId]?.cards.at(-1);
  if (!a || !b) return false;
  if (a.rank + b.rank !== 13) return false;
  discardCard(state, pileAId);
  discardCard(state, pileBId);
  state.movesCount++;
  return true;
}

function applyKing(state: GameState, fromPileId: string): boolean {
  const card = state.piles[fromPileId]?.cards.at(-1);
  if (!card || card.rank !== 13) return false;
  discardCard(state, fromPileId);
  state.movesCount++;
  return true;
}

function pileOfSelected(state: GameState): string | null {
  if (!selectedId) return null;
  return findPileWithCard(state, selectedId)?.id ?? null;
}

function clickPyramidOrWaste(
  state: GameState,
  pileId: string,
  cardId: string,
): PileClickResult {
  const pile = state.piles[pileId]!;
  if (pile.type === 'peak' && !isUncovered(state, pileId)) {
    return false;
  }
  const top = pile.cards.at(-1);
  if (!top || top.id !== cardId) return false;

  // Kings discard alone, no partner needed.
  if (top.rank === 13) {
    if (selectedId === cardId) selectedId = null;
    if (!applyKing(state, pileId)) return false;
    return true;
  }

  // Deliberate pair: an active selection wins over the auto-pair-with-waste
  // shortcut so the user can pair pyramid-to-pyramid without going via waste.
  if (selectedId && selectedId !== cardId) {
    const otherPileId = pileOfSelected(state);
    if (otherPileId && applyPair(state, otherPileId, pileId)) {
      selectedId = null;
      return true;
    }
  }

  // Auto-pair-with-waste shortcut: a single click on a card whose rank
  // complements the visible waste card discards both immediately. Standard
  // behavior in every shipped Pyramid app.
  const wasteTop = state.piles.waste!.cards.at(-1);
  if (wasteTop && wasteTop.id !== cardId && wasteTop.rank + top.rank === 13) {
    if (applyPair(state, pileId, 'waste')) {
      selectedId = null;
      return true;
    }
  }

  // Click the already-selected card → deselect.
  if (selectedId === cardId) {
    selectedId = null;
    return { commit: false };
  }

  // No instant action available - park selection on this card and wait for
  // a partner click (pyramid-to-pyramid pair).
  selectedId = cardId;
  return { commit: false };
}

// Wrap the rules so engine-level resets (new game, daily, replay) also clear
// the UI-only selection - the selection isn't part of GameState, so this is
// the cleanest hook to reset it without polluting the engine.
const wrappedRules: SolitaireRules = {
  ...pyramidRules,
  setup(deck) {
    selectedId = null;
    return pyramidRules.setup(deck);
  },
};

export const pyramidSpec: GameSpec = {
  rules: wrappedRules,
  cameraPad: 1.30,
  cameraElevationDeg: 62,
  slotForPile: (id) => layout.slot(id),
  // No drag - every move is a click.
  dropExclude: () => [...PYRAMID_IDS, 'stock', 'waste', 'foundation'],
  dragStackIds: () => [],
  autoSendOrder: () => ['foundation'],
  maxFanLength: 1,

  onPileClick(state, pile, cardId) {
    if (pile.id === 'stock') {
      // Drawing from stock implicitly clears any selection - the new waste
      // top becomes the focus for the next pair.
      selectedId = null;
      if (!drawFromStock(state)) return false;
      state.movesCount++;
      return true;
    }
    if (pile.type === 'peak' || pile.type === 'waste') {
      return clickPyramidOrWaste(state, pile.id, cardId);
    }
    return false;
  },

  cardOffset(pile, indexInPile) {
    const card = pile.cards[indexInPile];
    if (card && card.id === selectedId) {
      return { dx: 0, dz: 0, dy: SELECTED_LIFT };
    }
    return { dx: 0, dz: 0 };
  },

  statusText(state) {
    const stockEmpty = state.piles.stock!.cards.length === 0;
    if (stockEmpty && !hasAnyMove(state) && !pyramidRules.isWon(state)) {
      return 'No moves left';
    }
    if (selectedId) {
      const card = findPileWithCard(state, selectedId)?.cards.at(-1);
      if (card) {
        return `${rankLabel(card.rank)} selected - pick a ${rankLabel(13 - card.rank)}`;
      }
    }
    return null;
  },

  cascadeCardIds(state) {
    return state.piles.foundation!.cards.map((c) => c.id);
  },

  showSlotMarker: (id) => id === 'stock' || id === 'waste',
};

function rankLabel(r: number): string {
  if (r === 1) return 'A';
  if (r === 11) return 'J';
  if (r === 12) return 'Q';
  if (r === 13) return 'K';
  return String(r);
}
